import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { IColumnConfig } from '@/types/admin/dataTable';
import { IUserManagementTableRow, userTypeOptions } from '@/types/admin/userManagement';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { DesktopOverflowText } from '@/components/common/admin/desktop/DesktopOverflowText';
import { getUserTypeColor } from '@/constants/admin/userRoles';
import { summarizeList, listToTooltip } from '@/utils/admin/tableFormatters';

const useColumnStyles = () =>
  useThemedStyles(({ components }) =>
    StyleSheet.create({
      nameText: {
        fontSize: 16,
        fontWeight: '600',
        color: components.admin.dataCell.text,
      },
      emailText: {
        fontSize: 14,
        color: components.admin.dataCell.text,
      },
      emailTextRight: {
        textAlign: 'right',
      },
      userTypeBadge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 12,
        backgroundColor: components.admin.formInput.bg,
      },
      userTypeText: {
        fontSize: 14,
        fontWeight: '600',
      },
      userTypeTextRight: {
        textAlign: 'right',
      },
    }),
  );

const NameCell: React.FC<{ value: string }> = ({ value }) => {
  const styles = useColumnStyles();
  return (
    <Text style={styles.nameText} numberOfLines={2}>
      {value}
    </Text>
  );
};

const UserTypeCell: React.FC<{ value: string }> = ({ value }) => {
  const styles = useColumnStyles();
  return (
    <View style={styles.userTypeBadge}>
      <Text style={[styles.userTypeText, { color: getUserTypeColor(value) }]}>{value}</Text>
    </View>
  );
};

const UserTypeMobileCell: React.FC<{ value: string }> = ({ value }) => {
  const styles = useColumnStyles();
  return (
    <Text
      style={[styles.userTypeText, styles.userTypeTextRight, { color: getUserTypeColor(value) }]}
      numberOfLines={1}
    >
      {value}
    </Text>
  );
};

const EmailCell: React.FC<{ value: string }> = ({ value }) => {
  const styles = useColumnStyles();
  return (
    <Text style={styles.emailText} numberOfLines={1} ellipsizeMode="middle">
      {value}
    </Text>
  );
};

const ListSummaryCell: React.FC<{
  values: (string | null)[] | undefined;
  fallbackTooltipPrefix?: string;
}> = ({ values, fallbackTooltipPrefix }) => {
  const safe = (values || []).filter((v): v is string => !!v);
  if (safe.length === 0) return null;
  const displayText = summarizeList(safe);
  const tooltip = listToTooltip(safe, fallbackTooltipPrefix);
  return <DesktopOverflowText text={displayText} tooltipText={tooltip} numberOfLines={2} />;
};

const ListSummaryMobileCell: React.FC<{ values: (string | null)[] | undefined }> = ({ values }) => {
  const styles = useColumnStyles();
  const safe = (values || []).filter((v): v is string => !!v);
  if (safe.length === 0) {
    return (
      <Text style={[styles.emailText, styles.emailTextRight]} numberOfLines={1}>
        —
      </Text>
    );
  }
  return (
    <Text style={[styles.emailText, styles.emailTextRight]} numberOfLines={2}>
      {summarizeList(safe)}
    </Text>
  );
};

export function useUserManagementTableColumns(): IColumnConfig<IUserManagementTableRow>[] {
  return useMemo<IColumnConfig<IUserManagementTableRow>[]>(
    () => [
      {
        key: 'name',
        label: 'NAME',
        sortable: true,
        filterable: false,
        visible: true,
        mobileVisible: true,
        sortType: 'string',
        renderCell: (value: string) => <NameCell value={value} />,
      },
      {
        key: 'userType',
        label: 'USER TYPE',
        sortable: true,
        filterable: true,
        visible: true,
        mobileVisible: true,
        sortType: 'string',
        filterType: 'select',
        filterOptions: userTypeOptions.map((type) => ({
          label: type,
          value: type,
        })),
        renderCell: (value: string) => <UserTypeCell value={value} />,
        renderMobileCell: (value: string) => <UserTypeMobileCell value={value} />,
      },
      {
        key: 'email',
        label: 'EMAIL',
        sortable: true,
        filterable: true,
        visible: true,
        mobileVisible: true,
        sortType: 'string',
        renderCell: (value: string) => <EmailCell value={value} />,
      },
      {
        key: 'organization',
        label: 'ORGANIZATION',
        sortable: true,
        filterable: true,
        visible: true,
        mobileVisible: true,
        sortType: 'string',
        filterType: 'text',
        renderCell: (value: (string | null)[] | undefined) => (
          <ListSummaryCell values={value} fallbackTooltipPrefix="Org" />
        ),
        renderMobileCell: (value: (string | null)[] | undefined) => (
          <ListSummaryMobileCell values={value} />
        ),
      },
      {
        key: 'properties',
        label: 'PROPERTIES',
        sortable: true,
        filterable: true,
        visible: true,
        mobileVisible: true,
        sortType: 'string',
        filterType: 'text',
        renderCell: (value: (string | null)[] | undefined) => <ListSummaryCell values={value} />,
        renderMobileCell: (value: (string | null)[] | undefined) => (
          <ListSummaryMobileCell values={value} />
        ),
      },
    ],
    [],
  );
}
